import { useState, useCallback, useRef } from 'react';

/**
 * useControllable — support both controlled (`value` + `onChange`) and
 * uncontrolled (`defaultValue`) patterns for any interactive component.
 *
 * If `value` is provided, the component is controlled: the hook returns
 * the external value and a setter that only calls `onChange`. Otherwise
 * the hook manages internal state initialised with `defaultValue`.
 *
 * @example
 * // Inside a Select component
 * function Select({ value, defaultValue, onChange, ... }) {
 *   const [selected, setSelected] = useControllable(value, defaultValue ?? null, onChange);
 *   // `selected` is always the current value
 *   // `setSelected(newVal)` updates internal state (if uncontrolled) AND calls onChange
 * }
 */
export function useControllable<T>(
  controlled: T | undefined,
  defaultValue: T,
  onChange?: (value: T) => void,
): [T, (next: T) => void] {
  const isControlled = controlled !== undefined;
  const isControlledRef = useRef(isControlled);

  // Warn on mode switches in development
  if (import.meta.env?.DEV) {
    if (isControlledRef.current !== isControlled) {
      console.warn(
        '[useControllable] Component switched from %s to %s. This is not supported.',
        isControlledRef.current ? 'controlled' : 'uncontrolled',
        isControlled ? 'controlled' : 'uncontrolled',
      );
    }
  }

  const [internal, setInternal] = useState<T>(defaultValue);

  const value = isControlled ? controlled : internal;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) {
        setInternal(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [value, setValue];
}
